/**
 * Central route paths and helpers for React Router.
 * Paths are used for navigation and for matching (e.g. sidebar active state).
 */

/** Single set of routes. Used everywhere for nav and routing. */
export const ROUTES = {
  home: "/",
  dashboard: "/",
  login: "/login",
  logout: "/logout",
  resetPassword: "/reset-password",
  notFound: "/404",
  apply: "/apply",
  onboarding: "/onboarding",
  properties: "/properties",
  activities: "/activities",
  /** Deep-link to Activity / service report detail on Service History (`?report=`). */
  activitiesReport: (reportId: string) => `/activities?report=${encodeURIComponent(reportId)}`,
  schedule: "/schedule",
  docs: "/docs",
  settings: "/settings",
  adminUsers: "/admin/users",
  adminAdmins: "/admin/admins",
  // Note: admin/admins removed — unified into /admin/users with tabs (IF-422)
  adminProperties: "/admin/properties",
  adminProperty: (id: string) => `/admin/properties/${id}`,
  adminTime: "/schedule?tab=time",
  adminProtocols: "/admin/protocols",
  adminPredictions: "/admin/predictions",
  billing: "/billing",
  billingSuccess: "/billing/success",
  // Creator-only routes (previously /admin/*, now /creator/*)
  creatorLeads: "/creator/leads",
  creatorManifest: "/creator/manifest",
  creatorTopica: "/creator/topica",
  creatorDocs: "/creator/docs",
  creatorFunnel: "/creator/funnel",
  creatorToolsFeatures: "/creator/tools-features",
  creatorCapacitor: "/creator/capacitor",
  welcome: "/welcome",
  // Preview / embed routes — public, chromeless, iframeable from Framer
  previewGlitch: "/preview/glitch",
  myTrace: "/my-trace",
  traceFeed: "/trace",
  trace: (slug: string) => `/trace/${slug}`,
} as const

/** Pages that render inside the app layout (sidebar/topbar). Unknown paths → "404" so 404 is full-screen. */
const KNOWN_PAGES = new Set([
  "home", "login", "logout", "apply", "apply/enterprise", "welcome", "reset-password", "404", "onboarding",
  "schedule", "activities", "properties", "settings", "docs",
  "admin/users", "admin/admins",
  "admin/properties", "admin/time", "admin/protocols", "admin/predictions",
  "creator/leads", "creator/manifest", "creator/topica", "creator/docs", "creator/funnel", "creator/tools-features", "creator/capacitor",
  "preview/glitch",
  "my-trace",
  "billing", "billing/success",
])

/**
 * Map pathname to a "page" key and optional recordId for compatibility with existing Router logic.
 * Unknown paths return page "404" so the 404 renders without the app shell.
 */
export function pathnameToPage(pathname: string): { page: string; recordId: string | undefined } {
  const path = pathname.replace(/\?.*$/, "").replace(/^\//, "").replace(/\/$/, "") || "home"
  const segments = path.split("/")
  if (segments.length >= 3) {
    const base = segments.slice(0, 2).join("/")
    const id = segments[2]
    if (id && id !== "new" && /^[a-f0-9-]+$/i.test(id)) {
      const page = KNOWN_PAGES.has(base) ? base : "404"
      return { page, recordId: id }
    }
  }
  if (segments.length === 2 && (segments[1] === "new" || segments[0] === "test")) {
    const page = segments[0] + "/" + segments[1]
    return { page: KNOWN_PAGES.has(page) || page.startsWith("test/") ? page : "404", recordId: undefined }
  }
  const page = path === "" ? "home" : path
  const resolved = KNOWN_PAGES.has(page) || page.startsWith("docs/") || page.startsWith("test/") ? page : "404"
  return { page: resolved, recordId: undefined }
}

export function isKnownRoute(pathname: string): boolean {
  const { page } = pathnameToPage(pathname)
  if (KNOWN_PAGES.has(page)) return true
  if (page === "docs" || page.startsWith("docs/")) return true
  if (page.startsWith("admin/clients/") || page.startsWith("admin/properties/") || page.startsWith("admin/reports/")) return true
  if (page.startsWith("creator/")) return true
  return false
}

/**
 * Build path for navigation. "home" -> "/", "admin" -> "/admin/users", "admin/properties" -> "/admin/properties", "admin/properties" + id -> "/admin/properties/:id"
 */
export function pageToPath(page: string, recordId?: string): string {
  if (page === "home" || page === undefined || page === null || page === "") return "/"
  if (page === "admin") return "/admin/users"
  const base = "/" + page
  if (recordId) return base + "/" + recordId
  return base
}

/**
 * Convert a navigation target (e.g. "admin/properties/123" or "home") to a path for useNavigate.
 */
export function navTargetToPath(to: string): string {
  const trimmed = to.replace(/^\//, "").trim()
  if (!trimmed || trimmed === "home") return "/"
  if (trimmed === "admin") return "/admin/users"
  const segments = trimmed.split("/")
  if (segments.length >= 3) {
    const base = segments.slice(0, 2).join("/")
    const id = segments[2]
    return pageToPath(base, id)
  }
  return "/" + trimmed
}
